import { ConsolidatedReportComponent } from './reports/consolidated/consolidated-report.component';
import { TransferRequestsComponent } from './logistics/transfer-requests/transfer-requests.component';
import { Routes } from '@angular/router';

import { AuthenticationGuard } from '@shared/services/auth/authentication.guard';
import { NoAuthGuard } from '@shared/services/auth/no-auth.guard';

// Shell con sidebar
import { LayoutComponent } from './layout/layout.component';

// Login (fuera del layout)
import { LoginComponent } from './home/login/login.component';

// Módulos propios de Medtronic
import { DashboardComponent } from './dashboard/dashboard.component';
import { ScheduleComponent } from './schedule/schedule.component';
import { InterventionsComponent } from './interventions/interventions.component';
import { InterventionsMenuComponent } from './interventions/menu/interventions-menu.component';
import { InterventionsWizardComponent } from './interventions/interventions-wizard/interventions-wizard.component';
import { RemissionWizardComponent } from './interventions/remission-wizard/remission-wizard.component';
import { RemissionsComponent } from './remissions/remissions.component';
import { InventoryComponent } from './inventory/inventory.component';
import { InventoryCedisComponent } from './inventory-cedis/inventory-cedis.component';
import { InventoryEquipmentComponent } from './inventoryEquipments/inventoryEquipments.component';
import { InventoryInstrumentalsComponent } from './inventoryInstrumentals/inventoryInstrumentals.component';
import { ContractsComponent } from './contracts/contracts.component';

// Reportes y auditoría (Medtronic)
import { ReportsComponent } from './reports/reports.component';
import { ReportsMenuComponent } from './reports/menu/reports-menu.component';
import { AuditComponent } from './audit/audit.component';
import { AuditMenuComponent } from './audit/menu/audit-menu.component';
import { HistoryAuditComponent } from './audit/history-audit/history-audit.component';
import { RFIDMovementComponent } from './audit/rfid-movements/rfid-movements.component';
import { AccessHistoryComponent } from './audit/access-history/access-history.component';

// Soporte: tickets de servicio y manuales de usuario por rol
import { SupportComponent } from './support/support.component';
import { SupportMenuComponent } from './support/menu/support-menu.component';
import { SupportTicketsComponent } from './support/tickets/support-tickets.component';
import { UserManualsComponent } from './support/manuals/user-manuals.component';

// Catálogos y administración (portados desde Biomédica)
import { CataloguesComponent } from './catalogues/catalogues.component';
import { CataloguesMenuComponent } from './catalogues/menu/catalogues-menu.component';
import { CatalogueTemplateComponent } from './catalogues/catalogue-template/catalogue-template.component';
import { UsersComponent } from './users/users.component';
import { RolesComponent } from './roles/roles.component';
import { PermissionsComponent } from './permissions/permissions.component';
import { ModulesComponent } from './modules/modules.component';
import { MaterialReceptionComponent } from './material-reception/material-reception.component';
import { IncidentsComponent } from './incidents/incidents.component';
import { MaterialRequestsComponent } from './logistics/material-requests/material-requests.component';
import { TransfersComponent } from './logistics/transfers/transfers.component';
import { CustodyComponent } from './logistics/custody/custody.component';

/**
 * Catálogos disponibles. Todos se resuelven con `CatalogueTemplateComponent`,
 * que decide qué entidad mostrar leyendo el último segmento de la URL.
 */
const CATALOGUE_PATHS = [
  'warehouses',
  'rfidDevices',
  'inhouses',
  'clients',
  'products',
  'equipments',
  'manufacturers',
  'brands',
  'affiliate',
  'instrumentals',
  'procedures',
  'suppliers',
  'spareParts',
  'places',
  'specialties',
  'hospitalServices',
  'kits',
  'measurementInstruments',
  'medicalGasTanks',
  'measurementUnits',
  'paymentMethods',
  'courses',
  'taxRegimes',
  'accessories',
  'peripheralEquipment',
  'fixedAssetConfigurations',
  'fixedAssetServiceConfigurations',
  'supplies',
  'institutions',
  'doctors',
  'substitutes',
];

/** Reportes de Medtronic, resueltos por `ReportsTemplateComponent`. */

export const routes: Routes = [
  // ─── Login (sin sidebar) ───────────────────────────────────────────────
  { path: '', component: LoginComponent, canActivate: [NoAuthGuard] },

  // ─── Rutas autenticadas (sidebar persistente) ──────────────────────────
  // Se adopta el esquema de URL de Biomédica (sin el prefijo `/evolution`
  // que imponía el shell de Module Federation). Los enlaces viejos siguen
  // funcionando gracias a los redirects del final.
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthenticationGuard],
    children: [
      { path: 'dashboard', component: DashboardComponent },
      { path: 'schedule', component: ScheduleComponent },
      // Salida de material: menú → Folios / Remisiones (cada uno con su tabla)
      { path: 'interventions', component: InterventionsMenuComponent },
      { path: 'interventions/folios', component: InterventionsComponent, data: { mode: 'folio' } },
      { path: 'interventions/remissions', component: InterventionsComponent, data: { mode: 'remission' } },
      // Asistente de 5 pasos: alta completa con borrador reanudable
      { path: 'interventions/wizard', component: InterventionsWizardComponent },
      { path: 'interventions/wizard/:id', component: InterventionsWizardComponent },
      // Proceso de remisión dentro de Salida de material (crear / retomar borrador)
      { path: 'interventions/remission', component: RemissionWizardComponent },
      { path: 'interventions/remission/:id', component: RemissionWizardComponent },
      // Remisiones: documento comercial (directas y las nacidas del asistente)
      { path: 'remissions', component: RemissionsComponent },

      // Entradas de material (DLV / otro) fuera de Inventario
      { path: 'materialReception', component: MaterialReceptionComponent },

      // Incidencias de insumos y equipo/instrumental
      { path: 'incidents', component: IncidentsComponent },
      // Logística: solicitud de material, traspasos entre cuentas, resguardo y devolución
      { path: 'materialRequests', component: MaterialRequestsComponent },
      { path: 'transfers', component: TransfersComponent },
      { path: 'transferRequests', component: TransferRequestsComponent },
      { path: 'custody', component: CustodyComponent },
      // Inventarios: productos (consumible), instrumental y equipo
      { path: 'inventory', component: InventoryComponent },
      { path: 'inventoryCedis', component: InventoryCedisComponent, data: { sapKind: 'CONSIGNA' } },
      { path: 'inventorySapPlanta', component: InventoryCedisComponent, data: { sapKind: 'PLANTA' } },
      { path: 'inventoryInstrumentals', component: InventoryInstrumentalsComponent },
      { path: 'inventoryEquipments', component: InventoryEquipmentComponent },

      // Módulos que existen pero que hoy NO se muestran en el menú
      // (ver FALLBACK_MODULES en layout.component.ts). La ruta se conserva
      // para no romper enlaces y para poder reactivarlos cuando toque.
      { path: 'contracts', component: ContractsComponent },

      // Catálogos (router-outlet anidado dentro de CataloguesComponent)
      {
        path: 'catalogues',
        component: CataloguesComponent,
        children: [
          { path: 'menu', component: CataloguesMenuComponent },
          ...CATALOGUE_PATHS.map((path) => ({ path, component: CatalogueTemplateComponent })),
          { path: '', redirectTo: 'menu', pathMatch: 'full' as const },
        ],
      },

      // Reportes
      {
        path: 'reports',
        component: ReportsComponent,
        children: [
          { path: 'menu', component: ReportsMenuComponent },
          { path: 'remissions', component: ConsolidatedReportComponent, data: { report: 'remissions' } },
          { path: 'folios', component: ConsolidatedReportComponent, data: { report: 'folios' } },
          { path: 'deliveriesVsSap', component: ConsolidatedReportComponent, data: { report: 'deliveriesVsSap' } },
          { path: 'incidentsByStatus', component: ConsolidatedReportComponent, data: { report: 'incidentsByStatus' } },
          { path: 'incidentsByLot', component: ConsolidatedReportComponent, data: { report: 'incidentsByLot' } },
          { path: '', redirectTo: 'menu', pathMatch: 'full' as const },
        ],
      },

      // Auditoría
      {
        path: 'audit',
        component: AuditComponent,
        children: [
          { path: 'menu', component: AuditMenuComponent },
          { path: 'history', component: HistoryAuditComponent },
          { path: 'rfid', component: RFIDMovementComponent },
          { path: 'access', component: AccessHistoryComponent },
          { path: '', redirectTo: 'menu', pathMatch: 'full' },
        ],
      },

      // Soporte (router-outlet anidado dentro de SupportComponent)
      //   /support/tickets  → levantar y dar seguimiento a tickets
      //   /support/manuals  → manual en PDF y videos por módulo, según el rol
      {
        path: 'support',
        component: SupportComponent,
        children: [
          { path: 'menu', component: SupportMenuComponent },
          { path: 'tickets', component: SupportTicketsComponent },
          { path: 'manuals', component: UserManualsComponent },
          { path: '', redirectTo: 'menu', pathMatch: 'full' },
        ],
      },

      // Administración (modelo de Biomédica: usuarios, roles, permisos, módulos)
      { path: 'users', component: UsersComponent },
      { path: 'roles', component: RolesComponent },
      { path: 'permissions', component: PermissionsComponent },
      { path: 'modules', component: ModulesComponent },
    ],
  },

  // ─── Compatibilidad con las URLs de la etapa microfrontend ─────────────
  // El shell montaba esta app bajo `/evolution`. Se conservan como redirect
  // para que no se rompan enlaces guardados.
  { path: 'evolution/auditory', redirectTo: 'audit' },
  { path: 'evolution', redirectTo: '' },

  // Catch-all → login (que a su vez redirige al tablero si ya hay sesión)
  { path: '**', redirectTo: '' },
];
